import { Component, Input } from '@angular/core';
import { FormControl } from '@angular/forms';
import { TypedFormGroup } from '@feature/forms/form-model';
import { dateFormat } from '@core/config';
import { TranslateService } from '@ngx-translate/core';
import { ValueFormatterService } from '@shared/value-formatter/value-formatter.service';

@Component({
  selector: 'wai-form-error',
  templateUrl: './form-error.component.html',
  styleUrls: ['./form-error.component.scss'],
  standalone: false,
})
export class FormErrorComponent {
  public asTypedFormGroup = (x: unknown): TypedFormGroup<any> => x as TypedFormGroup<any>;

  public dateFormat = dateFormat;
  @Input() public formField: FormControl = null;

  public get compareTranslation(): string {
    const error = this.formField.getError('compare');
    const key = error?.requiredPredicate?.toUpperCase();
    const field2 = this.getFieldLabel(error?.field2);

    return this.translate.instant(`INTL_FORM_ERROR_COMPARE_${key}`, { field2 });
  }

  public get compareToSumTargetTranslation(): string {
    const error = this.formField.getError('compareToSumTarget');
    const key = error?.requiredPredicate?.toUpperCase();
    const field1 = this.getFieldLabel(error?.field1);
    const field2 = this.getFieldLabel(error?.field2);

    return this.translate.instant(`INTL_FORM_ERROR_COMPARE_TO_SUM_TARGET_${key}`, { field1, field2 });
  }

  public get compareToSumSummandTranslation(): string {
    const error = this.formField.getError('compareToSumSummand');
    const key = error?.requiredPredicate?.toUpperCase();
    const field1 = this.getFieldLabel(error?.field1);
    const field2 = this.getFieldLabel(error?.field2);

    return this.translate.instant(`INTL_FORM_ERROR_COMPARE_TO_SUM_SUMMAND_${key}`, { field1, field2 });
  }

  public get phoneTranslation(): string {
    const key = this.formField.getError('phone');
    return this.translate.instant(`INTL_FORM_ERROR_PHONE_${key}`);
  }

  public get divisibleByTranslation(): string {
    return this.getCurrencyRelatedTranslation('divisibleBy', 'INTL_FORM_ERROR_DIVISIBLE_BY');
  }

  public get minIncrementTranslation(): string {
    return this.getCurrencyRelatedTranslation('minIncrement', 'INTL_FORM_ERROR_MIN_INCREMENT');
  }

  public get minCurrencyValueTranslation(): string {
    return this.getCurrencyRelatedTranslation('minCurrencyValue', 'INTL_FORM_ERROR_MIN_CURRENCY_VALUE');
  }

  public get maxCurrencyValueTranslation(): string {
    return this.getCurrencyRelatedTranslation('maxCurrencyValue', 'INTL_FORM_ERROR_MAX_CURRENCY_VALUE');
  }

  public get dateOfBirthFutureTranslation(): string {
    return this.translate.instant('INTL_FORM_ERROR_DATE_OF_BIRTH_FUTURE');
  }

  public get dateOfBirthTooYoungTranslation(): string {
    const error = this.formField.getError('dateOfBirth');
    return this.translate.instant('INTL_FORM_ERROR_DATE_OF_BIRTH_TOO_YOUNG', {
      actualAge: error?.actualAge,
    });
  }

  public get dateOfBirthTooOldTranslation(): string {
    const error = this.formField.getError('dateOfBirth');
    return this.translate.instant('INTL_FORM_ERROR_DATE_OF_BIRTH_TOO_OLD', {
      actualAge: error?.actualAge,
    });
  }

  constructor(private translate: TranslateService, private formatter: ValueFormatterService) {}

  /**
   * Converts a field name to its translated label.
   * Tries multiple translation key patterns and falls back to the field name if no translation is found.
   */
  private getFieldLabel(fieldName: string | undefined): string {
    if (!fieldName) {
      return '';
    }

    // Special case mappings for common field names
    const specialMappings: Record<string, string> = {
      password: 'INTL_LOGIN_FORM_LABEL_PASSWORD',
      confirm: 'INTL_USER_PASSWORD_DIALOG_REPEAT',
      oldPassword: 'INTL_USER_PASSWORD_DIALOG_OLD',
      newPassword: 'INTL_USER_DETAILS_BUTTON_NEW_PASSWORD',
      repeatPassword: 'INTL_USER_PASSWORD_DIALOG_REPEAT',
    };

    // Check special mappings first
    if (specialMappings[fieldName]) {
      const translation = this.translate.instant(specialMappings[fieldName]);
      if (translation !== specialMappings[fieldName]) {
        return translation;
      }
    }

    // Handle special naming conventions
    const normalizedName = fieldName
      .replace(/^min([A-Z])/, 'minimum$1') // min -> minimum
      .replace(/^max([A-Z])/, 'maximal$1'); // max -> maximal

    // Common field name patterns to try
    const translationKeys = [
      // Direct uppercase with INTL_ prefix (e.g., minInvestment -> INTL_MINIMUM_INVESTMENT)
      `INTL_${this.camelCaseToSnakeCase(normalizedName).toUpperCase()}`,
      // Original field name pattern
      `INTL_${this.camelCaseToSnakeCase(fieldName).toUpperCase()}`,
      // With product opening prefix (e.g., maxInvestment -> INTL_PRODUCT_OPENING_FORM_INPUT_MAXIMAL_INVESTMENT)
      `INTL_PRODUCT_OPENING_FORM_INPUT_${this.camelCaseToSnakeCase(normalizedName).toUpperCase()}`,
    ];

    // Try each translation key
    for (const key of translationKeys) {
      const translation = this.translate.instant(key);
      // Check if translation was found (instant returns the key if not found)
      if (translation !== key) {
        return translation;
      }
    }

    // Fall back to humanized field name (e.g., minInvestment -> Min Investment)
    return this.humanizeFieldName(fieldName);
  }

  /**
   * Converts camelCase to snake_case
   */
  private camelCaseToSnakeCase(str: string): string {
    return str.replace(/[A-Z]/g, (letter) => `_${letter.toLowerCase()}`);
  }

  /**
   * Converts a field name to a human-readable format
   */
  private humanizeFieldName(fieldName: string): string {
    // Convert camelCase to separate words, and add space before numbers
    return fieldName
      .replace(/([A-Z])/g, ' $1')
      .replace(/([0-9])/g, ' $1')
      .replace(/^./, (str) => str.toUpperCase())
      .trim();
  }

  private getCurrencyRelatedTranslation(errorKey: string, translateKey: string): string {
    const value = this.formField.getError(errorKey);
    return this.translate.instant(translateKey, {
      value: this.formatter.format('currency', value).formattedValue,
    });
  }
}
